// NPM
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useRecoilState } from 'recoil';
import { useParams, Link } from 'react-router-dom';
import moment from 'moment';
import { omitBy, isEmpty, isNil, map, sortBy, find, forEach, isArray } from 'lodash';
import { Helmet } from "react-helmet";

// HELPERS
import { encode, decode } from 'Helpers/enc';

// HOOKS
import useQuery from 'Hooks/query.hook';

// RECOIL STATE
import { categoryAtom, bestDealsAtom, hotSellingItemsAtom, inventoryAtom } from 'Recoil/inventory.atom';
import { orgAtom } from 'Recoil/org.atom';
import { bannerListAtom } from 'Recoil/banner.atom';

// SERVICE
import { fetchCategoryList, fetchProductList, fetchProductSectionList } from 'Services/inventory.services';
import { fetchOrgList } from 'Services/org.services';
import { fetchBanners } from 'Services/banner.services';
import { getVoucherList } from 'Services/voucher.service';

import { Grid, Space, Row, Col, Radio, Divider, Carousel } from 'antd';

// COMPONENTS
import ProductListingNew from "Components/product/product-listing-new";
import ProductCatalog from "Components/product-catalog";
import VoucherListSellerComponent from 'Components/voucher-list/seller';
import { statusChatAtom } from 'Recoil/chat.atom';

// STYLE
import './homepage.scss';
import VacationInfoBox from 'Components/shared/vacation-info-box/vacation-info-box';

interface HomepageProps {}

const Homepage: React.FC<HomepageProps> = () => {
  // VARIABLE DECLARATION
  const history = useHistory();
  const query = useQuery();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();

  const name: any = query.get('name');
  const category: any = query.get('category');

  const supplierID = decode(id);
  const categoryID = decode(category)

  const breakpoint = Grid.useBreakpoint();
  const initialFilters = omitBy({ orgID: supplierID, categoryIDs: categoryID ? [categoryID] : null }, isNil)

  // USE STATE
  const [pagination, setPagination] = useState<{ offset: number; limit: number }>({ offset: 0, limit: 100 })
  const [voucherListSeller, setVoucherListSeller] = useState<any>([]);

  // USE RECOIL STATE
  const [suppliersState, setSuppliersState] = useRecoilState<any>(orgAtom);
  const [slidersState, setSlidersState] = useRecoilState<any>(bannerListAtom);
  const [categoryState, setCategoryState] = useRecoilState<any>(categoryAtom);
  const [productsState, setProductsState] = useRecoilState<any>(inventoryAtom);
  const [bestDealsState, setBestDealsState] = useRecoilState<any>(bestDealsAtom);
  const [hotSellingItemsState, setHotSellingItemsState] = useRecoilState<any>(hotSellingItemsAtom);
  const [chatStatusState, setChatStatusState] = useRecoilState<any>(statusChatAtom);

  // INITIALISE VARIABLE
  const isSupplierLoading = suppliersState.status.fetchList === 'idle'
    && suppliersState.status.fetchList === 'loading';

  const isProductsLoading = productsState.status.fetchList === 'idle'
    && productsState.status.fetchList === 'loading';

  const isBestDealsLoading = bestDealsState.status.fetchList === 'idle'
    && bestDealsState.status.fetchList === 'loading';

  const isHotSellingItemsLoading = hotSellingItemsState.status.fetchList === 'idle'
    && hotSellingItemsState.status.fetchList === 'loading';

  // INIT
  useEffect(() => {
    handleFetchCategories();
    handleFetchProductSectionList('BEST_DEALS');
    handleFetchProductSectionList('HOT_SELLING_ITEMS');
    handleFetchSupplier();
    handleFetchSliders();
    voucherListFunc();
  }, []);

  useEffect(() => {
    handleFetchProduct();
  }, [category])

  const handleFetchSupplier = async () => {
    setSuppliersState({
      ...suppliersState,
      status: { ...suppliersState.status, fetchList: 'loading' }
    });

    const responseData = await fetchOrgList(omitBy({ orgIDs: supplierID ? [supplierID] : null }, isNil));

    if (!isNil(responseData)) {
      setSuppliersState({
        ...suppliersState,
        status: { ...suppliersState.status, fetchList: 'success' },
        list: responseData.data.orgs[0]
      });
    }
    else {
      setSuppliersState({
        ...suppliersState,
        status: { ...suppliersState.status, fetchList: 'error' }
      });
    }
  }

  const handleFetchSliders = async () => {
    setSlidersState({
      ...slidersState,
      status: { ...slidersState.status, fetchList: 'loading' }
    });

    const responseData = await fetchBanners({
      filters: omitBy({ orgID: supplierID, isLandingPage: false, bannerType: 'SLIDER', }, isNil),
      sorting: { sortBy: 'position', mode: 'ASC' }
    });

    if (!isNil(responseData)) {
      let sliders: any = []
      let sideBanners: any = []
      let images: any = []
      let files: any = []

      forEach(responseData!.data?.banners, banner => {
        images = banner.images
        files = banner.files_data

        if (banner.bannerType == 'SLIDER') {

          sliders = map((sortBy(images, 'position')), (image, i) => {
            return {
              key: images[i].fileID,
              href: images[i].redirectURL,
              imageUrl: find(files, (file) => file._id == images[i].fileID)?.file_url
            }
          })

        }
      })

      setSlidersState({
        ...slidersState,
        status: { ...slidersState.status, fetchList: 'success' },
        list: { sliders, sideBanners }
      });

    } else {
      setSlidersState({
        ...slidersState,
        status: { ...slidersState.status, fetchList: 'error' }
      });
    }

  }

  const handleFetchCategories = async () => {
    setCategoryState({
      ...categoryState,
      status: { ...categoryState.status, fetch: 'loading' }
    });

    const responseData = await fetchCategoryList({ level: [1], active: true, orgID: supplierID });

    if (!isNil(responseData)) {
      setCategoryState({
        ...categoryState,
        status: { ...categoryState.status, fetch: 'success' },
        list: responseData
      });
    }
    else {
      setCategoryState({
        ...categoryState,
        status: { ...categoryState.status, fetch: 'error' }
      });
    }
  }

  const handleFetchProduct = async (filters?: any) => {

    filters = { ...initialFilters, ...filters }

    setProductsState({
      ...productsState,
      status: { ...productsState.status, fetch: 'loading' }
    });

    const responseData = await fetchProductList({ ...filters }, pagination);

    if (!isNil(responseData)) {
      setProductsState({
        ...productsState,
        status: { ...productsState.status, fetch: 'success' },
        list: responseData
      });
    }
    else {
      setProductsState({
        ...productsState,
        status: { ...productsState.status, fetch: 'error' }
      });
    }
  }

  const handleFetchProductSectionList = async (type: string) => {
    if (type === 'BEST_DEALS') {
      setBestDealsState({
        ...bestDealsState,
        status: { ...bestDealsState.status, fetchList: 'loading' }
      });

      const responseData = await fetchProductSectionList({
        filters: { type, isLandingPage: false, orgID: supplierID }
      });

      if (!isNil(responseData)) {
        setBestDealsState({
          ...bestDealsState,
          status: { ...bestDealsState.status, fetchList: 'success' },
          list: responseData
        });
      }
      else {
        setBestDealsState({
          ...bestDealsState,
          status: { ...bestDealsState.status, fetchList: 'error' }
        });
      }
    }
    else if (type === 'HOT_SELLING_ITEMS') {
      setHotSellingItemsState({
        ...hotSellingItemsState,
        status: { ...hotSellingItemsState.status, fetchList: 'loading' }
      });

      const responseData = await fetchProductSectionList({
        filters: { type, isLandingPage: false, orgID: supplierID }
      });

      if (!isNil(responseData)) {
        setHotSellingItemsState({
          ...hotSellingItemsState,
          status: { ...hotSellingItemsState.status, fetchList: 'success' },
          list: responseData
        });
      }
      else {
        setHotSellingItemsState({
          ...hotSellingItemsState,
          status: { ...hotSellingItemsState.status, fetchList: 'error' }
        });
      }
    }
  }

  // VOUCHER LIST FUNCTION
  const voucherListFunc = async () => {
    // FILTER
    const filter = {filters : {
        voucherBy: "SELLER",
        sourceSite: process.env.ACCOUNT_TYPE,
        orgID: supplierID
    }};

    // GET VOUCHER LIST API
    const responseSellerData = await getVoucherList(filter);
    // OUTPUT === ARRAY => VALID RESPONSE, SET VALUES
    if (isArray(responseSellerData)) {
        setVoucherListSeller(responseSellerData);
    }
  }

  const handleCategorySelect = (data: any) => {
    history.push(`/supplier/${id}?name=${encode(data.name)}&category=${encode(data._id)}`);
  }

  const handleClearCategorySelect = () => {
    history.push(`/supplier/${id}`);
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>
          {t('title.appTitle')}
        </title>
      </Helmet>

      <div className="supplier--homepage">

        <div className="gradient-bg" />

        {!isEmpty(suppliersState.list) &&
          <div className="supplier-details">
            <div className="supplier-details--inner">

              <div className="supplier-info">

                {!isSupplierLoading &&

                  <React.Fragment>
                    <Row gutter={[10, 20]}>
                      <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                        <div className="supplier-info--inner">
                          <Row gutter={8}>
                            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                              <div className="name">
                                <Link to={`/supplier/${id}`}>
                                  <img
                                    alt={t('alt.logo')}
                                    src={suppliersState.list.image_file?.file_url || '/assets/images/product-placeholder.png'}
                                  />
                                </Link>

                                <Link to={`/supplier/${id}`}>
                                  <h2>{suppliersState.list.name}</h2>
                                </Link>
                              </div>
                            </Col>

                            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                              {suppliersState.list.primaryAddress?.city ?
                                <div className="description">
                                  <p>
                                    {t('txt.basedIn')} {suppliersState.list.primaryAddress?.city}, {suppliersState.list.name} {t('txt.merchantSince')} {moment(suppliersState.list.createdAt).format('DD MMM, YYYY')}
                                  </p>
                                </div> : null}
                            </Col>

                            <Col xxl={24} xl={24} lg={24} md={24} sm={24} xs={24}>
                              {
                                // if is vacation
                                suppliersState.list?.ecomSetting?.vacationMode &&
                                <VacationInfoBox 
                                  infoOption={suppliersState.list?.ecomSetting?.vacationOrderHandling} 
                                  type={
                                    suppliersState.list?.ecomSetting?.vacationOrderHandling === "CHECKOUT_NOT_ALLOWED" ?
                                      "warning" :
                                      "info"
                                  }
                                />
                              }
                            </Col>

                          </Row>

                          <Row>
                            <div className="icons">
                              <div>
                                <img src='/assets/images/supplier/regulated.svg' width={14} /> &nbsp;
                                {t('txt.fullyRegulated')}
                              </div>
                              <div>
                                <img src='/assets/images/supplier/secure.svg' width={15} /> &nbsp;
                                {t('txt.secure')} &amp; {t('txt.trusted')}
                              </div>
                            </div>
                          </Row>

                        </div>
                      </Col>

                      <Col xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
                        <div className="supplier-banner">
                          <div className="sliders">
                            {slidersState.list &&
                              <Carousel autoplay={true}>
                                {map(slidersState.list.sliders, (slider, index) => {
                                  return (
                                    <div className="sliders--item" key={slider.key ? slider.key : index} style={{ margin: 'auto' }}>
                                      <a>
                                        <img src={slider.imageUrl} onClick={() => window.open(slider.href)} />
                                      </a>
                                    </div>
                                  )
                                })}
                              </Carousel>
                            }
                          </div>
                        </div>
                      </Col>

                    </Row>
                  </React.Fragment>

                }
              </div>

            </div>
          </div>
        }

        {!isEmpty(bestDealsState.list) &&
          <div className="best-deals">
            <div className="container">
              <h2 className="title">
                {t('title.bestDeals')}
              </h2>

              {!isBestDealsLoading && <React.Fragment>
                <div className="product-listing-new">
                  <Row gutter={24}>
                    {map(bestDealsState.list, (o, index) => {
                      const price = parseFloat(o.stockVariants[0]?.value);
                      const stockLimit = o.stockVariants[0]?.stockLimit ? parseInt(o.stockVariants[0]?.stockLimit) : 0;
                      const percentOff = 10;
                      const discountPrice = price + (price * 10 * 0.01);
                      const discounted = o.discountPrice
                        ? (parseFloat(o.price) - parseFloat(o.discountPrice)).toFixed(2) : 0;
                      return (
                        <Col lg={5} md={5} sm={12} xs={12}>
                          <ProductListingNew
                            product={{
                              displayType: 'saved',
                              imageUrl: o.featuredImage?.image,
                              name: o.product.productName,
                              productType: o.product.productType,
                              price: discountPrice.toFixed(2),
                              stockLimit: stockLimit,
                              discountPrice: price.toFixed(2),
                              percentOff: percentOff,
                              productData: o,
                              supplierLocation: o.orgData?.primaryAddress?.city || null,
                              disablePurchase: o.disablePurchase,
                              href: `/products/${encode(o.inventoryID)}`,
                              discounted: discounted
                            }}
                          />
                        </Col>
                      )
                    })}
                  </Row>
                </div>
              </React.Fragment>}
            </div>
          </div>
        }

        {
          isArray(voucherListSeller) && !isEmpty(voucherListSeller) ?
            <div style={{width: "90%", margin: "auto"}}>
              <VoucherListSellerComponent hideTitle={false} voucherList={voucherListSeller}></VoucherListSellerComponent>
            </div>
            : null
        }

        {!isEmpty(hotSellingItemsState.list) &&
          <div className="hot-selling">
            <div className="container">
              <h2 className="title">
                {t('title.hotSellingItems')}
              </h2>

              {!isHotSellingItemsLoading && <React.Fragment>
                <div className="product-listing-new">
                  <Row gutter={24}>
                    {map(hotSellingItemsState.list, (o, index) => {
                      const price = parseFloat(o.stockVariants[0]?.value);
                      const stockLimit = o.stockVariants[0]?.stockLimit ? parseInt(o.stockVariants[0]?.stockLimit) : 0;
                      const percentOff = 10;
                      const discountPrice = price + (price * 10 * 0.01);
                      const discounted = o.discountPrice
                        ? (parseFloat(o.price) - parseFloat(o.discountPrice)).toFixed(2) : 0;
                      return (
                        <Col lg={5} md={5} sm={12} xs={12}>
                          <ProductListingNew
                            product={{
                              displayType: 'saved',
                              imageUrl: o.featuredImage?.image,
                              name: o.product.productName,
                              productType: o.product.productType,
                              price: discountPrice.toFixed(2),
                              stockLimit: stockLimit,
                              discountPrice: price.toFixed(2),
                              percentOff: percentOff,
                              productData: o,
                              supplierLocation: o.orgData?.primaryAddress?.city || null,
                              disablePurchase: o.disablePurchase,
                              href: `/products/${encode(o.inventoryID)}`,
                              discounted: discounted
                            }}
                          />
                        </Col>
                      )
                    })}
                  </Row>
                </div>
              </React.Fragment>}
            </div>
          </div>
        }

        <div className="products">
          <div className="container">
            <h2 className="title">
              {t('title.allProducts')}
            </h2>

            <ProductCatalog
              isLevel1Only={true}
              isLoading={isProductsLoading}
              products={productsState.list || []}
              categories={categoryState.list || []}
              onCategorySelect={handleCategorySelect}
              onClearCategorySelect={handleClearCategorySelect}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default Homepage;
